<template>
    <div v-if="isWarning || isLocked || isLoggedOut">
        <div class="screen-lock-container">
            <div class="screen-lock-inner-container">
                <transition name="fade">
                    <div v-if="showControls">
                        <avatar 
                            :size="96" 
                            :user="currentUser" 
                        />
                        <h1>{{ currentUser.name }}</h1>
                        <div v-if="isLoggedOut">
                            <div>You have been logged out</div>
                            <ui-button 
                                class="screen-lock-clickable" 
                                size="large" 
                                icon="lock_open"
                                @click="$root.navigateTo('logout')"
                            >
                                Login
                            </ui-button>
                        </div>
                        <div v-else-if="isLocked">
                            <p v-if="passwordError" class="text-warning">
                                These credentials do not match our records.
                            </p>
                            <p v-if="requestError" class="text-danger">
                                There was an error unlocking your account, try again.
                            </p>
                            <input 
                                v-model="password" 
                                type="password" 
                                class="form-control screen-lock-clickable" 
                                placeholder="Enter password"
                                @keypress.enter="unlock"
                            >
                            <ui-button 
                                size="large" 
                                icon="lock_open"
                                :loading="isValidating"
                                @click="unlock" 
                            >
                                Unlock
                            </ui-button>
                            <a 
                                href="#" 
                                class="screen-lock-clickable"
                                @click.prevent="logout" 
                            >Login as a different user</a>
                        </div>
                        <div v-else-if="isWarning">
                            <div>Locking screen in {{ secondsUntilRevalidation | friendlySeconds }}</div>
                            <ui-button 
                                class="screen-lock-clickable" 
                                size="large" 
                                icon="lock_open"
                                :loading="isValidating"
                                @click="cancel" 
                            >
                                Cancel
                            </ui-button>
                        </div>
                    </div>
                </transition>
            </div>
        </div>
    </div>
</template>

<style lang="scss">

body {
    main, nav, header {
        transition: 1s filter ease-in-out, opacity .5s;
        -webkit-transition: 1s -webkit-filter ease-in-out, opacity .5s;
        filter: none;
        -webkit-filter: none;
        opacity: 1;
    }
}

.screen-lock-container {
    .fade-enter-active, .fade-leave-active {
      transition: opacity 1s, background 1s;
    }
    .fade-enter, .fade-leave-to /* .fade-leave-active below version 2.1.8 */ {
      opacity: 0;
    }
}

body.screen-locked {
    overflow: hidden;
    
    main, nav, header {
        filter: grayscale(100%); //blur(4px);
        -webkit-filter: grayscale(100%); //blur(4px);
        opacity: .35;
    }

    .screen-lock-container {
        background:rgba(0,0,0,0.8);
        .screen-lock-inner-container { transform: translate(-50%, -50%); h1 { color: #fff; } color: #fff; }
    }
}
    .screen-lock-container {
        height: 100vh; width: 100vw; 
        position: fixed; 
        z-index: 9999; 
        top: 0; left: 0; 
        background:rgba(0,0,0,0);
        backdrop-filter: blur(3px);
        -webkit-backdrop-filter: blur(3px);

        .screen-lock-clickable {
            display: block; 
            margin: 1rem auto;
        }

        .screen-lock-inner-container {
            position: absolute; 
            top: 50%; left: 50%; 
            transform: translate(-50%, -50%); 
            text-align: center;
            width: 300px;
        }
    }
</style>

<script>
    import Moment from 'moment'

    export default {
        filters: {
            friendlySeconds(value) {
                let mins = 0;
                if (value / 60 > 1) {
                    mins = Math.floor(value / 60)
                    value -= mins * 60
                }

                let str = ''

                if (1 === mins) {
                    str = '1 minute ';
                } else if (mins > 1) {
                    str = mins + ' minutes '
                }

                if (value > 0) {
                    str += value + ' seconds'
                }

                return str
            }
        },
        data() {
            return {
                grace: 35,
                timeout: null,
                isLocked: false,
                isWarning: false,
                isLoggedOut: false,
                lastPing: null,
                lastMovement: null,
                secondsUntilRevalidation: null,
                password: '',
                passwordError: false,
                requestError: false,
                isPinging: false,
                //sessionExpired: false,
                lastSessionRevalidation: null,
                sessionLength: null,
                isValidating: false,
                showControls: false,
            }
        },
        computed: {
            currentUser() {
                return window.marque.ui.user;
            }
        },
        mounted() {
            let config = window.marque.ui.revalidation

            this.timeout = config.timeout ? config.timeout * 60 : config.session_length * 60
            this.sessionLength = config.session_length * 60
            this.lastPing = this.lastSessionRevalidation = new Moment()
            this.secondsUntilRevalidation = this.timeout

            this.$root.$on('unauthenticated', () => {
                this.logout()
            })

            setInterval(() => {
                this.secondsUntilRevalidation = this.secondsUntilRevalidationRequired()

                let revalidationRequiredAt = (new Moment()).subtract(this.timeout - this.grace, 'seconds')

                let warnAt = (new Moment(this.lastMovement)).add(this.timeout - this.grace, 'seconds')

                let lastMovementExpired = (new Moment()).diff(this.lastMovement, 'seconds') >= this.grace

                if (!this.isLoggedOut && this.hasSessionExpired()) {
                    this.logout()
                } else if(!this.isLocked && this.secondsUntilRevalidation <= 0) {
                    this.lock();
                } else if (!this.isWarning && !this.isLocked && lastMovementExpired && (new Moment()).isAfter(warnAt)) {
                    this.warn();
                } else if(!this.isLocked && !this.isWarning && !this.isLoggedOut && this.lastPing.isBefore(revalidationRequiredAt)) {
                    this.ping();
                }
            }, 100);

            this.movement();

            document.addEventListener('mousemove', this.movement, false);
            document.addEventListener('keydown', this.movement, false);
            document.addEventListener('scroll', this.movement, false);
        },
        methods: {
            sessionExpiry() {
                return (
                    new Moment(this.lastSessionRevalidation)
                ).add(this.sessionLength, 'seconds')
            },
            hasSessionExpired() {
                return this.sessionExpiry().isBefore(new Moment());
            },
            secondsUntilRevalidationRequired() {
                return (
                    new Moment(this.lastPing)
                ).add(
                    this.timeout, 'seconds'
                ).diff(
                    (new Moment), 'seconds'
                )
            },
            addLockedBodyClass() {
                if (this.showControls) {
                    return
                }

                document.querySelector('body').className = 
                    document.querySelector('body').className + ' screen-locked'

                setTimeout(() => {
                    this.showControls = true
                }, 1000)
            },
            removeLockedBodyClass() {
                this.showControls = false
                
                setTimeout(() => {
                    document.querySelector('body').className = 
                        document.querySelector('body').className.replace(' screen-locked', '')
                }, 1000)
            },
            movement() {
                this.lastMovement = new Moment()
            },
            ping(force = false) {
                return new Promise((resolve, reject) => {
                    if ((force || !this.isWarning && !this.isLocked) && !this.isPinging) {
                        this.isPinging = true

                        window.http.get(window.marque.ui.baseEndpoint + '/ping').then((r) => {
                            this.lastPing = this.lastSessionRevalidation = new Moment()
                            this.isPinging = false
                            resolve()
                        }, (e) => { 
                            if (e.response && e.response.status === 401) {
                                this.sessionLength = 0
                                this.logout()
                            }

                            this.isPinging = false; reject(e) 
                        })
                    }
                })
            },
            logout() {
                if (!this.isLoggedOut) {
                    this.isLoggedOut = true;
                    this.isLocked = false;
                    this.isWarning = false;
                    this.addLockedBodyClass();
                    window.marque.ui.authenticated = false
                }
            },
            warn() {
                if (!this.isWarning) {
                    this.isWarning = true
                    this.addLockedBodyClass()
                }
            },
            lock() {
                if (!this.isLocked) {
                    this.isLocked = true
                    this.isWarning = false
                    this.addLockedBodyClass();
                    window.marque.ui.authenticated = false
                }
            },
            cancel() {
                this.isValidating = true;
                this.ping(true).then(r => {
                    this.removeLockedBodyClass()
                    this.movement()
                    setTimeout(() => {
                        this.isLocked = false
                        this.isWarning = false
                        this.isLoggedOut = false
                        this.isValidating = false
                        this.password = ''
                    }, 1000)
                })
            },
            unlock() {
                this.passwordError = false
                this.requestError = false
                this.isValidating = true

                let url = window.marque.ui.baseEndpoint + '/users/me/challenge'

                window.http.post(url, { password: this.password }).then((r) => {
                    if ('success' === r.data.result) {
                        this.cancel()
                        window.marque.ui.authenticated = true
                    } else if ('not_logged_in' === r.data.result) {
                        this.password = '';
                        this.sessionLength = 0
                        this.isValidating = false
                        this.passwordError = false
                        this.logout()
                    } else {
                        this.passwordError = true
                        this.isValidating = false;
                        this.password = ''
                    }
                }, e => {
                    this.requestError = true
                    this.isValidating = false;
                    this.password = ''
                })
            }
        }
    }
</script>